import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { SessionHistory } from '../components/NeoLeo/NeoLeoHistoryIndex';
import {
  ChatApi,
  MessageActionRequest,
  PromptLibraryRequest,
  SearchActionRequest,
  SearchApi,
  VoteType,
} from '../openapi/leo';
import AnalyticsService from '../services/AnalyticsService';
import ErrorService from '../services/ErrorService';
import {
  AnalyticsEventEnum,
  AppThunk,
  CurrentChat,
  LeoState,
  RootState,
  SenderReceiverEnum,
  SessionWithDisclaimer,
} from '../types';
import { getAuthCookie } from '../utils/AuthUtils';
import { performFetch } from '../utils/FetchUtils';
import {
  CallSupportDetails,
  ContextualPrompt,
  DEFAULT_CHAT_PROMPTS,
  DONE_TOKEN,
  FollowUp,
  FollowUpItem,
  getLeoResponseStreamUrl,
  getLeoSupportResponseSteamUrl,
  LeoResponseMessageContext,
  LeoShareablesConfig,
  START_TOKEN,
  UserIntentEnum,
} from '../utils/LeoUtils';
import Logger from '../utils/Logger';
import { getLeoConfiguration } from '../utils/OpenapiConfigurationUtils';
import { showErrorToast, showSuccessToast } from './ToastNotificationSlice';

export const initialState: LeoState = {
  isNeoLeoOpen: false,
  isNeoLeoExpanded: false,
  openNeoLeo: false,
  loading: false,
  isSessionLoading: false,
  isChatLoading: false,
  isSessionError: false,
  session: undefined,
  showIntro: true,
  chatHistory: [],
  isHistoryLoading: false,
  currentChat: [],
  selectedItems: [],
  deletingSessionId: undefined,
  isLeoGenerating: false,
  isLeoThinking: false,
  isTicketCreated: false,
  msgContext: undefined,
  isLeoModalVisible: false,
  isNudgeEnabled: false,
  defaultPrompts: [],
  callSupportDetails: undefined,
  isCreatingTicket: false,
  isLatestLeoResponseExtensive: false,
  shareables: {
    msgId: '',
    activeShareableIndex: 0,
    showShareablesModal: false,
  },
  showShareablesModal: false,
};

export const leoSlice = createSlice({
  name: 'leo',
  initialState,
  reducers: {
    openNeoLeo: (state, action: PayloadAction<boolean>) => {
      state.isNeoLeoOpen = action.payload;
    },
    closeNeoLeo: (state) => {
      state.isNeoLeoOpen = false;
    },
    changeLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    changeIsSessionLoading: (state, action: PayloadAction<boolean>) => {
      state.isSessionLoading = action.payload;
    },
    changeIsChatLoading: (state, action: PayloadAction<boolean>) => {
      state.isChatLoading = action.payload;
    },
    saveIsSessionError: (state, action: PayloadAction<boolean>) => {
      state.isSessionError = action.payload;
    },
    saveSession: (
      state,
      action: PayloadAction<SessionWithDisclaimer | undefined>,
    ) => {
      state.session = action.payload;
    },
    saveShowIntro: (state, action: PayloadAction<boolean>) => {
      state.showIntro = action.payload;
    },
    saveSessionHistory: (state, action: PayloadAction<SessionHistory[]>) => {
      state.chatHistory = action.payload;
    },
    changeIsHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.isHistoryLoading = action.payload;
    },
    saveCurrentChat: (state, action: PayloadAction<CurrentChat[]>) => {
      state.currentChat = action.payload;
    },
    saveDeletingSessionId: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.deletingSessionId = action.payload;
    },
    deleteChatHistory: (state, action: PayloadAction<string>) => {
      state.chatHistory = state.chatHistory?.filter(
        (el) => el.id !== action.payload,
      );
    },
    saveResetLeo: (state) => {
      state.showIntro = true;
      state.session = undefined;
      state.currentChat = [];
    },
    saveIsLeoGenerating: (state, action: PayloadAction<boolean>) => {
      state.isLeoGenerating = action.payload;
    },
    saveIsLeoThinking: (state, action: PayloadAction<boolean>) => {
      state.isLeoThinking = action.payload;
    },
    saveIsTicketCreated: (state, action: PayloadAction<boolean>) => {
      state.isTicketCreated = action.payload;
    },
    updateCurrentChat: (state, action: PayloadAction<CurrentChat>) => {
      const chatIndex = state.currentChat?.findIndex(
        (el) => el.id === action.payload.id,
      );
      if (chatIndex < 0) {
        state.currentChat = [...state.currentChat, action.payload];
      } else {
        state.currentChat[chatIndex] = action.payload;
      }
    },
    saveMsgContext: (
      state,
      action: PayloadAction<LeoResponseMessageContext | undefined | null>,
    ) => {
      state.msgContext = action.payload || undefined;
    },
    updateSessionHistoryTitle: (
      state,
      action: PayloadAction<{ title: string; session_id: string }>,
    ) => {
      const chatHistoryIndex = state.chatHistory.findIndex(
        (el) => el.id === action.payload.session_id,
      )!;
      if (chatHistoryIndex >= 0) {
        state.chatHistory![chatHistoryIndex] = {
          ...state.chatHistory![chatHistoryIndex],
          title: action.payload.title,
        };
      } else {
        state.chatHistory = [
          ...state.chatHistory,
          {
            title: action.payload.title,
            agent_id: state.session?.agent_id! as string,
            id: action.payload.session_id,
            start_time: new Date().toISOString(),
            system_message: '',
          },
        ];
      }
    },
    saveSelectedItems: (state, action: PayloadAction<FollowUpItem[]>) => {
      state.selectedItems = action.payload;
    },
    saveIsLeoModalVisible: (state, action: PayloadAction<boolean>) => {
      state.isLeoModalVisible = action.payload;
    },
    saveIsNudgeEnabled: (state, action: PayloadAction<boolean>) => {
      state.isNudgeEnabled = action.payload;
    },
    saveDefaultPrompts: (state, action: PayloadAction<ContextualPrompt[]>) => {
      state.defaultPrompts = action.payload;
    },
    saveCallSupportDetails: (
      state,
      action: PayloadAction<CallSupportDetails | undefined>,
    ) => {
      state.callSupportDetails = action.payload;
    },
    saveIsCreatingTicket: (state, action: PayloadAction<boolean>) => {
      state.isCreatingTicket = action.payload;
    },
    setIsLatestLeoResponseExtensive: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isLatestLeoResponseExtensive = action.payload;
    },
    setShareablesConfig: (
      state,
      action: PayloadAction<LeoShareablesConfig>,
    ) => {
      state.shareables = {
        ...state.shareables,
        ...action.payload,
      };
    },
    setShowShareablesModal: (state, action: PayloadAction<boolean>) => {
      state.shareables.showShareablesModal = action.payload;
    },
  },
});

export const {
  openNeoLeo,
  closeNeoLeo,
  changeLoading,
  changeIsSessionLoading,
  changeIsChatLoading,
  saveIsSessionError,
  saveSession,
  saveShowIntro,
  saveSessionHistory,
  changeIsHistoryLoading,
  saveCurrentChat,
  saveDeletingSessionId,
  deleteChatHistory,
  saveResetLeo,
  saveIsLeoGenerating,
  saveIsLeoThinking,
  saveIsTicketCreated,
  updateCurrentChat,
  saveMsgContext,
  updateSessionHistoryTitle,
  saveSelectedItems,
  saveIsLeoModalVisible,
  saveIsNudgeEnabled,
  saveDefaultPrompts,
  saveCallSupportDetails,
  saveIsCreatingTicket,
  setIsLatestLeoResponseExtensive,
  setShareablesConfig,
  setShowShareablesModal,
} = leoSlice.actions;

const ChatController = ChatApi;

export const createChatSession =
  (
    agent_id: string,
    notification_id?: string,
  ): AppThunk<Promise<string | undefined>> =>
  async (dispatch) => {
    dispatch(changeIsSessionLoading(true));
    try {
      const { data: session } = await new ChatController(
        await getLeoConfiguration(),
      ).createChatSessionChatSessionPost({ agent_id, notification_id });
      const id = session.id as string;
      dispatch(
        saveSession({
          ...session,
          disclaimer: session.disclaimer ?? '',
          id,
          entrypoint: 'Chat',
        }),
      );
      dispatch(saveIsSessionError(false));
      return id;
    } catch (e) {
      dispatch(saveIsSessionError(true));
      dispatch(
        showErrorToast(
          'We had a problem creating the chat session.',
          'Please try again in a few moments.',
        ),
      );
      ErrorService.notifyIgnoreAuthErrors(
        'unable to create chat session',
        e as Error,
      );
      return;
    } finally {
      dispatch(changeIsSessionLoading(false));
    }
  };

export const fetchSessionHistory =
  (agent_id: string): AppThunk =>
  async (dispatch) => {
    dispatch(changeIsHistoryLoading(true));
    try {
      const { data } = await new ChatController(
        await getLeoConfiguration(),
      ).getAllAgentSessionsChatSessionGet(agent_id);
      dispatch(saveSessionHistory(data as any as SessionHistory[]));
    } catch (e) {
      dispatch(
        showErrorToast(
          'We had a problem fetching session history.',
          'Please try again in a few moments.',
        ),
      );
      ErrorService.notify('unable to fetch sesion history', e as Error);
    } finally {
      dispatch(changeIsHistoryLoading(false));
    }
  };

export const fetchSessionChat =
  (session_id: string, agent_id: string): AppThunk =>
  async (dispatch) => {
    dispatch(changeIsChatLoading(true));
    try {
      const { data } = await new ChatController(
        await getLeoConfiguration(),
      ).getMessagesChatMessagesGet(session_id);
      dispatch(
        saveSession({
          id: session_id,
          agent_id,
          disclaimer: '',
          entrypoint: 'Chat',
        }),
      );
      dispatch(saveCurrentChat(data as any as CurrentChat[]));
    } catch (e) {
      dispatch(
        showErrorToast(
          'We had a problem fetching the chat session.',
          'Please try again in a few moments.',
        ),
      );
      ErrorService.notify('unable to fetch chat sesion', e as Error);
    } finally {
      dispatch(changeIsChatLoading(false));
    }
  };

export const deleteSessionHistory =
  (id: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch(saveDeletingSessionId(id));
    try {
      await new ChatController(
        await getLeoConfiguration(),
      ).deleteSessionChatSessionSessionIdDelete(id);
      dispatch(deleteChatHistory(id));
    } catch (e) {
      dispatch(
        showErrorToast(
          'We had a problem deleting this session.',
          'Please try again in a few moments.',
        ),
      );
      ErrorService.notify('unable to delete message', e as Error);
    } finally {
      dispatch(saveDeletingSessionId(undefined));
    }
  };

export const updateChatQuestion =
  (session_id: string, question: string): AppThunk =>
  (dispatch) => {
    const created_at = new Date().toISOString();
    dispatch(
      updateCurrentChat({
        id: uuid(),
        session_id,
        message: question,
        sender: SenderReceiverEnum.USER,
        created_at,
      }),
    );
  };
export const updateChatResponse =
  (session_id: string, responseId: string, message: string): AppThunk =>
  (dispatch) => {
    dispatch(
      updateCurrentChat({
        id: responseId,
        session_id,
        message,
        created_at: new Date().toISOString(),
        sender: SenderReceiverEnum.SYSTEM,
      }),
    );
  };

export const chatReply =
  (
    session_id: string,
    question: string,
    updateSessionTitle: boolean = false,
    prompt: ContextualPrompt | undefined,
    followUps?: FollowUp[],
    source?: string,
    supportIntent?: UserIntentEnum,
    notificationId?: string,
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      leo: { isLeoGenerating },
    }: RootState = getState();
    const jwt = getAuthCookie();
    dispatch(saveIsLeoThinking(true));
    dispatch(saveIsTicketCreated(false));
    dispatch(updateChatQuestion(session_id, question));
    dispatch(saveMsgContext(undefined));

    try {
      const streamUrl = getLeoResponseStreamUrl(
        question,
        session_id,
        followUps,
        prompt,
        source,
        supportIntent,
        notificationId,
      );

      const streamResponse = await performFetch(streamUrl, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'x-real-app-name': 'one-real-web',
        },
      });

      //@ts-ignore
      const reader = streamResponse.body
        .pipeThrough(new TextDecoderStream())
        .getReader();

      let hasMessageStarted = false;
      let isMessageDone = false;
      let msgContext: LeoResponseMessageContext | undefined = undefined;
      let responseId = '';
      let response = '';
      let jsonBuffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        const messages = value
          ?.split('\n')
          .filter((message) => message.indexOf('data: ') === 0)
          .map((message) => message.replace('data: ', '').trim());

        if (!messages) {
          continue;
        }

        for (const message of messages) {
          if (message === START_TOKEN) {
            hasMessageStarted = true;
            continue;
          } else if (message === DONE_TOKEN) {
            isMessageDone = true;
            dispatch(saveIsLeoThinking(false));
            dispatch(saveIsLeoGenerating(false));
            if (updateSessionTitle) {
              dispatch(
                updateSessionHistoryTitle({ session_id, title: question }),
              );
            }
            continue;
          } else if (!responseId) {
            responseId = JSON.parse(message).bot_message_id;
            continue;
          }

          if (isMessageDone) {
            try {
              msgContext = JSON.parse(
                `${jsonBuffer}${message}`,
              ) as LeoResponseMessageContext;
              dispatch(saveMsgContext(msgContext));
              jsonBuffer = '';
            } catch (e: any) {
              if (e.message.includes('Unterminated string in JSON')) {
                jsonBuffer = message;
              }
            }
            dispatch(saveSelectedItems([]));
            continue;
          }
          if (hasMessageStarted) {
            response = `${response}${message
              .slice(1, -1)
              .split(/\\n/g)
              .join('<br>')
              .replace('\\"', '"')}`;
          }
          if (hasMessageStarted && !isLeoGenerating) {
            dispatch(saveIsLeoGenerating(true));
          }
        }

        if (response) {
          dispatch(updateChatResponse(session_id, responseId, response));
        }
      }
    } catch (e: any) {
      dispatch(
        showErrorToast(
          'We had a problem getting a response from Leo.',
          'Please try again in a few moments.',
        ),
      );
      if (
        !e ||
        ['JSON Parse error: Unterminated string', 'Failed to fetch'].includes(e)
      ) {
        Logger.debug('[Event Stream]: no error specified. Skipping...', e);
        return;
      }

      ErrorService.notify('Failed to send/receive chat reply. Error - ', e);
      dispatch(saveSelectedItems([]));
    } finally {
      dispatch(saveIsLeoThinking(false));
    }
  };

export const openLeo =
  (
    agent_id: string,
    question?: string,
    prompt?: ContextualPrompt,
    followUps: FollowUp[] = [],
    notificationId?: string,
  ): AppThunk =>
  async (dispatch) => {
    if (!agent_id) {
      return;
    }
    dispatch(saveIsLeoModalVisible(true));
    dispatch(saveIsNudgeEnabled(false));
    const sessionId = await dispatch(
      createChatSession(agent_id, notificationId),
    );
    AnalyticsService.instance().logEvent(AnalyticsEventEnum.LEO_BAR_CLICK);
    if (!sessionId || !question) {
      return;
    }
    dispatch(saveShowIntro(false));
    dispatch(
      chatReply(
        sessionId,
        question,
        true,
        prompt,
        followUps,
        undefined,
        undefined,
        notificationId,
      ),
    );
  };

export const fetchPrompts =
  (
    promptsReq: PromptLibraryRequest,
  ): AppThunk<Promise<ContextualPrompt[] | undefined>> =>
  async (dispatch) => {
    dispatch(changeLoading(true));
    try {
      const { data } = await new ChatController(
        await getLeoConfiguration(),
      ).getPromptSuggestionsChatPromptLibraryPost(promptsReq);
      if (!data.items || !data.items.length) {
        throw new Error('No prompts found');
      }
      dispatch(saveDefaultPrompts(data.items as ContextualPrompt[]));
      return data.items as ContextualPrompt[];
    } catch (e) {
      ErrorService.notifyIgnoreAuthErrors(
        '[LEO] - Failed to fetch leo prompts.',
        e as Error,
      );
      dispatch(saveDefaultPrompts(DEFAULT_CHAT_PROMPTS as ContextualPrompt[]));
      return undefined;
    } finally {
      dispatch(changeLoading(false));
    }
  };

export const postKbarActionAnalytics =
  (req: SearchActionRequest): AppThunk =>
  async () => {
    try {
      await new SearchApi(getLeoConfiguration()).createActionSearchActionPost(
        req,
      );
    } catch (e) {
      ErrorService.notify(
        '[LEO] - Failed to post notification analytics',
        e as Error,
      );
    }
  };

export const supportReply =
  (
    supportIntent: UserIntentEnum,
    session_id: string,
    attachmentIds?: string[],
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      leo: { isLeoGenerating },
    }: RootState = getState();
    const jwt = getAuthCookie();
    if (!jwt) {
      dispatch(showErrorToast('You must be logged in to chat with Leo.'));
      ErrorService.notify('JWT not found. Unable to chat with leo', {
        name: 'jwtError',
        message: 'JWT undefined',
      });
    }
    dispatch(saveIsLeoThinking(true));
    dispatch(saveIsTicketCreated(false));
    dispatch(saveMsgContext(undefined));

    try {
      const streamUrl = getLeoSupportResponseSteamUrl(
        supportIntent,
        session_id,
        attachmentIds,
      );

      const streamResponse = await performFetch(streamUrl, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'x-real-app-name': 'one-real-web',
        },
      });

      //@ts-ignore
      const reader = streamResponse.body
        .pipeThrough(new TextDecoderStream())
        .getReader();

      let hasMessageStarted = false;
      let isMessageDone = false;
      let msgContext: LeoResponseMessageContext | undefined = undefined;
      let responseId = '';
      let response = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        const messages = value
          ?.split('\n')
          .filter((message) => message.indexOf('data: ') === 0)
          .map((message) => message.replace('data: ', '').trim());

        if (!messages) {
          continue;
        }

        for (const message of messages) {
          if (message === START_TOKEN) {
            hasMessageStarted = true;
            continue;
          } else if (message === DONE_TOKEN) {
            isMessageDone = true;
            dispatch(saveIsLeoThinking(false));
            dispatch(saveIsLeoGenerating(false));
            continue;
          } else if (!responseId) {
            responseId = JSON.parse(message).bot_message_id;
            continue;
          }

          if (isMessageDone) {
            msgContext = JSON.parse(message) as LeoResponseMessageContext;
            dispatch(saveMsgContext(msgContext));
            dispatch(saveSelectedItems([]));
            continue;
          }
          if (hasMessageStarted) {
            response = `${response}${message
              .slice(1, -1)
              .split(/\\n/g)
              .join('<br>')
              // .join('\n')
              .replace('\\"', '"')}`;
          }
          if (hasMessageStarted && !isLeoGenerating) {
            dispatch(saveIsLeoGenerating(true));
          }
        }

        if (response) {
          dispatch(updateChatResponse(session_id, responseId, response));
        }
      }
    } catch (e: any) {
      dispatch(
        showErrorToast(
          'We had a problem getting a response from Leo.',
          'Please try again in a few moments.',
        ),
      );
      if (!e) {
        Logger.debug('[Event Stream]: no error specified. Skipping...', e);
        return;
      }
      ErrorService.notify('unable to send/receive chat reply', e);
      dispatch(saveSelectedItems([]));
    } finally {
      dispatch(saveIsLeoThinking(false));
    }
  };

export const createTicket =
  (msgId: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch(saveIsCreatingTicket(true));
    try {
      await new ChatController(
        await getLeoConfiguration(),
      ).createTicketChatMessageMessageIdTicketPost(msgId);
      dispatch(saveIsTicketCreated(true));
      dispatch(showSuccessToast('Ticket created successfully'));
    } catch (e) {
      dispatch(
        showErrorToast('We had a problem creating a ticket. Pleas try again.'),
      );
      ErrorService.notify('[LEO] - unable to create ticket', e as Error);
    } finally {
      dispatch(saveIsCreatingTicket(false));
    }
  };

export const updateMessageVote =
  (message: CurrentChat, voteType: VoteType): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      dispatch(
        updateCurrentChat({
          ...message,
          vote_type: voteType === message.vote_type ? undefined : voteType,
        }),
      );

      const { data } = await new ChatController(
        await getLeoConfiguration(),
      ).voteChatMessageMessageIdVoteVoteTypePost(message.id!, voteType);

      dispatch(updateCurrentChat(data as any as CurrentChat));
    } catch (e) {
      dispatch(updateCurrentChat(message));
      dispatch(
        showErrorToast(
          'We had a problem upvoting/downvoting this message.',
          'Please try again in a few moments.',
        ),
      );
      ErrorService.notify('unable to upvote/downvote message', e as Error);
    }
  };

export const postMessageActionTaken =
  (msgId: string, msgAction: MessageActionRequest): AppThunk =>
  async () => {
    try {
      await new ChatApi(
        getLeoConfiguration(),
      ).actionChatMessageMessageIdActionPost(msgId, msgAction);
    } catch (e) {
      ErrorService.notify(
        '[LEO] - Failed to post message action taken',
        e as Error,
      );
    }
  };

export default leoSlice.reducer;
